/**
 * Authorization utility functions (web: client-side checks and API call)
 */

import { UserRole } from '@repo/shared-types';
import { logger } from '@repo/shared-utils';
import { getActiveNavIdFromPath } from './navigation';
import { getAllowedRolesForNavId } from '@repo/shared-config';
import { apiTransport } from 'services/api/transport';

export type AuthorizableResource =
  | { type: 'property'; propertyId: string; ownerIds?: string[] }
  | { type: 'unit'; unitId: string; propertyId: string }
  | { type: 'work_order'; assignedVendorId?: string; tenantId?: string; unitId?: string; propertyId?: string }
  | { type: 'bill'; vendorId?: string; propertyId?: string }
  | { type: 'tenant_resource'; tenantId: string; propertyId?: string; unitId?: string }
  | {
      type?: 'generic' | never;
      propertyId?: string;
      unitId?: string;
      ownerIds?: string[];
      assignedVendorId?: string;
      tenantId?: string;
      vendorId?: string;
    };

/** Ask the API whether the *authenticated* user may access `resource` (identity comes from Bearer context only). */
export const isAuthorized = async (resource: AuthorizableResource): Promise<boolean> => {
  try {
    const res = await apiTransport.post<{ authorized: boolean }>('/auth/check-authorization', {
      resource,
    });
    return res?.authorized ?? false;
  } catch (error) {
    logger.error('Authorization check failed:', error);
    return false;
  }
};

export const SMO_RESTRICTED_NAV_IDS = new Set(['owners.management', 'properties.owners']);

export const canAccessRoute = (
  userRole: UserRole,
  routePath: string,
  isSelfManagingOwner?: boolean
): boolean => {
  const navId = getActiveNavIdFromPath(routePath);
  if (isSelfManagingOwner === true && userRole === UserRole.PROPERTY_MANAGER && SMO_RESTRICTED_NAV_IDS.has(navId)) {
    return false;
  }
  const allowedRoles = getAllowedRolesForNavId(navId);
  if (!allowedRoles.length) {
    return false;
  }
  return allowedRoles.includes(userRole);
};
